import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  TextField,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Switch,
  FormControlLabel,
  Stack,
  Menu,
  MenuItem, // Added MenuItem
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { AttioTextField } from '@/components/AttioForm';
import {
  AddCircleOutline as AddIcon,
  RadioButtonChecked as RadioIcon,
  CheckBox as CheckboxIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ContentCopy as DuplicateIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QuestionType } from '@/types/questionTypes'; // Adjust path
import {
  UiQuestion,
  UiQuestionOption
} from '@/pages/TemplateEditPage/templateEditTypes';
import {
  getTypeIcon,
  getTypeLabel
} from '@/pages/TemplateEditPage/templateEditUtils';

interface SortableQuestionEditorProps {
  question: UiQuestion;
  index: number;
  onLocalQuestionChange: (
    id: number | string,
    updates: Partial<UiQuestion>
  ) => void;
  onPersistQuestion: (
    id: number | string,
    updates: Partial<UiQuestion>
  ) => void;
  onDuplicate: (id: number | string) => void;
  onDelete: (id: number | string) => void;
  onAddQuestion: (type: QuestionType) => void;
  isExpanded: boolean;
  toggleExpand: () => void;
}

const SortableQuestionEditor: React.FC<SortableQuestionEditorProps> = ({
  question,
  index,
  onLocalQuestionChange,
  onPersistQuestion,
  onDuplicate,
  onDelete,
  onAddQuestion,
  isExpanded,
  toggleExpand
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [currentTitle, setCurrentTitle] = useState(question.title);
  const [currentDescription, setCurrentDescription] = useState(
    question.description || ''
  );
  const [currentOptions, setCurrentOptions] = useState(
    question.options?.map((opt) => ({ ...opt })) || []
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setCurrentTitle(question.title);
    setCurrentDescription(question.description || '');
    setCurrentOptions(question.options?.map((opt) => ({ ...opt })) || []);
  }, [question.title, question.description, question.options]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 10 : 'auto'
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);
  
  const handleAccordionChange = (event: React.SyntheticEvent, expanded: boolean) => {
    if (!isDragging) {
      toggleExpand();
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { title: e.target.value });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentDescription(e.target.value);
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { description: e.target.value });
  };
  
  const handleSave = () => {
    onPersistQuestion(question.id, { 
      title: currentTitle,
      description: currentDescription,
      options: currentOptions
    });
    setHasUnsavedChanges(false);
  };
  
  const handleSaveAndAddAnother = () => {
    handleSave();
    // Close current question editor
    toggleExpand();
    // Add a new question of the same type
    setTimeout(() => {
      onAddQuestion(question.type);
    }, 100);
  };
  
  const handleCancel = () => {
    // Revert to original values
    setCurrentTitle(question.title);
    setCurrentDescription(question.description || '');
    setCurrentOptions(question.options?.map((opt) => ({ ...opt })) || []);
    setHasUnsavedChanges(false);
    toggleExpand(); // Close the editor
  };

  const handleOptionLabelChange = (
    optionId: number | string,
    value: string
  ) => {
    const updatedOptions = currentOptions.map((opt) =>
      opt.id === optionId ? { ...opt, label: value } : opt
    );
    setCurrentOptions(updatedOptions);
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { options: updatedOptions });
  };

  const handleAddOption = () => {
    const newOption: UiQuestionOption = {
      id: `temp_opt_${Date.now()}`,
      label: '',
      order: currentOptions.length
    };
    const newOptions = [...currentOptions, newOption];
    setCurrentOptions(newOptions);
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { options: newOptions });
  };

  const handleDeleteOption = (optionId: number | string) => {
    const newOptions = currentOptions.filter((opt) => opt.id !== optionId);
    setCurrentOptions(newOptions);
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { options: newOptions });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isRequired = e.target.checked;
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { required: isRequired });
  };

  const handleHasOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hasOther = e.target.checked;
    setHasUnsavedChanges(true);
    onLocalQuestionChange(question.id, { hasOther: hasOther });
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-question-id={question.id}
      sx={{
        mb: 1,
        ...(isDragging && {
          backgroundColor: theme.palette.action.hover
        })
      }}
      {...attributes} // Spread attributes for dnd-kit
    >
      <Accordion
        expanded={isExpanded}
        onChange={handleAccordionChange}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '6px',
          backgroundColor: theme.palette.background.paper,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0
          },
          '&:hover': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
          },
          ...(isDragging && {
            backgroundColor: theme.palette.action.hover
          })
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2,
            py: 1,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
              alignItems: 'center',
              '&.Mui-expanded': {
                margin: '12px 0'
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
            <Box
              {...listeners}
              sx={{
                display: 'flex',
                color: theme.palette.divider,
                cursor: 'grab',
                touchAction: 'none',
                '&:hover': { color: theme.palette.text.secondary }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <DragIndicatorIcon sx={{ fontSize: 18 }} />
            </Box>
            
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  textAlign: 'left'
                }}
              >
                {index + 1}. {question.title || `(Untitled Question)`}
                {question.required && (
                  <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
              
              {/* Question Type Tag */}
              <Box
                sx={{
                  px: 0.75,
                  py: 0.125,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: theme.palette.text.secondary,
                  '& svg': {
                    fontSize: '12px'
                  }
                }}>
                  {getTypeIcon(question.type)}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '9px',
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    textTransform: 'capitalize',
                    lineHeight: 1.2
                  }}
                >
                  {getTypeLabel(question.type)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="More options">
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMenu(e);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: theme.palette.text.secondary,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.08)' 
                        : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            p: 0,
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box
            sx={{
              maxWidth: 800,
              mx: 'auto',
              px: 3,
              py: 3
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <AttioTextField
                label="Question Title"
                value={currentTitle}
                onChange={handleTitleChange}
                required={question.required}
                autoFocus
                placeholder="Enter your question"
              />
              <AttioTextField
                label="Description (Optional)"
                value={currentDescription}
                onChange={handleDescriptionChange}
                multiline
                minRows={1}
                maxRows={4}
                placeholder="Add additional context or instructions"
              />
            </Box>
          {(question.type === QuestionType.Checkbox ||
            question.type === QuestionType.Radio ||
            question.type === QuestionType.Dropdown) && (
            <Box
              sx={{
                mt: 2,
                mb: 1,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontSize: '14px' }}>
                Options
              </Typography>
              {currentOptions.map((option, idx) => (
                <Box
                  key={option.id}
                  sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}
                >
                  {question.type === QuestionType.Radio && (
                    <RadioIcon
                      sx={{ mr: 1.5, color: 'text.disabled' }}
                      fontSize="small"
                    />
                  )}
                  {question.type === QuestionType.Checkbox && (
                    <CheckboxIcon
                      sx={{ mr: 1.5, color: 'text.disabled' }}
                      fontSize="small"
                    />
                  )}
                  {question.type === QuestionType.Dropdown && (
                    <Typography
                      variant="body2"
                      sx={{
                        mr: 1.5,
                        color: 'text.disabled',
                        width: '20px',
                        textAlign: 'center',
                        fontSize: '14px'
                      }}
                    >
                      {idx + 1}.
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    value={option.label}
                    onChange={(e) =>
                      handleOptionLabelChange(option.id, e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    placeholder={`Option ${idx + 1}`}
                    InputProps={{
                      sx: { fontSize: '14px' }
                    }}
                  />
                  <Tooltip title="Delete Option">
                    <span>
                      <IconButton
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => handleDeleteOption(option.id)}
                        disabled={(currentOptions?.length ?? 0) <= 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
              >
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                >
                  Add Option
                </Button>
                {(question.type === QuestionType.Checkbox ||
                  question.type === QuestionType.Radio) && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={question.hasOther || false}
                        onChange={handleHasOtherChange}
                      />
                    }
                    label="Allow 'Other'"
                    labelPlacement="start"
                    sx={{ 
                      mr: 0, 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '14px' 
                      }
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}
          
            {/* Required Toggle */}
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                px: 2,
                py: 1.5,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                }}
              >
                Required
              </Typography>
              <Switch
                checked={question.required}
                onChange={handleRequiredChange}
              />
            </Box>

            {/* Save Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
                mt: 3
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveAndAddAnother}
                disabled={!hasUnsavedChanges || !currentTitle.trim()}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                Save & Add Another
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || !currentTitle.trim()}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 160,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.5)'
                : '0 2px 8px rgba(0,0,0,0.15)',
              borderRadius: '8px'
            }
          }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onDuplicate(question.id);
            }}
          >
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onDelete(question.id);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              primaryTypographyProps={{ color: 'error' }}
            />
          </MenuItem>
        </Menu>
      </Accordion>
    </Box>
  );
};

export default SortableQuestionEditor;
